import React, { useMemo } from 'react';
import { FieldView } from '../../drilleditor/components/FieldView';
import { applyOrientation, type FieldPreset } from '../../drilleditor/lib/field';
import type { TimelineState } from '../../drilleditor/lib/timeline';
import type { Actor, XY, ExercisePitch } from '../../drilleditor/types';

interface ExerciseFieldPreviewProps {
  actors: Actor[];
  pitch?: ExercisePitch;
  variant?: 'indoor' | 'outdoor';
  markerScale?: number;
  preset?: FieldPreset;
  orientation?: 'left' | 'right';
  showLabels?: boolean;
  className?: string;
  style?: React.CSSProperties;
  playbackState?: Pick<TimelineState, 'actorPositions' | 'ballOwner' | 'flyingBall' | 'currentTime'> | null;
}

export default function ExerciseFieldPreview({
  actors,
  pitch,
  variant,
  markerScale = 1,
  preset = 'threequarters',
  orientation = 'right',
  showLabels = true,
  className,
  style,
  playbackState,
}: ExerciseFieldPreviewProps) {
  const miniGoalNumberMap = useMemo(() => {
    const map = new Map<string, number>();
    actors
      .filter((actor): actor is Extract<Actor, { kind: 'miniGoal' }> => actor.kind === 'miniGoal')
      .forEach((goal, index) => map.set(goal.id, index + 1));
    return map;
  }, [actors]);

  const getPosition = (actor: Actor) => {
    const playbackPosition = playbackState?.actorPositions.get(actor.id);
    if (playbackPosition) return applyOrientation(playbackPosition, orientation);
    const x = 'x' in actor && typeof actor.x === 'number' ? actor.x : 0;
    const y = 'y' in actor && typeof actor.y === 'number' ? actor.y : 0;
    return applyOrientation({ x, y }, orientation);
  };

  const ballActor = actors.find((actor): actor is Extract<Actor, { kind: 'ball' }> => actor.kind === 'ball');

  const renderBall = () => {
    if (!ballActor) return null;
    if (playbackState?.flyingBall) {
      const flyingBall = playbackState.flyingBall;
      const points: XY[] = [flyingBall.from, ...(flyingBall.via || []), flyingBall.to];
      const segmentPosition = flyingBall.progress * Math.max(1, points.length - 1);
      const segmentIndex = Math.min(Math.floor(segmentPosition), points.length - 2);
      const segmentProgress = segmentPosition - segmentIndex;
      const from = points[segmentIndex];
      const to = points[segmentIndex + 1];
      const position = applyOrientation({
        x: from.x + ((to.x - from.x) * segmentProgress),
        y: from.y + ((to.y - from.y) * segmentProgress),
      }, orientation);
      return <circle cx={position.x} cy={position.y} r={8 * markerScale} fill="#111827" stroke="#ffffff" strokeWidth={1} />;
    }
    const ownerId = playbackState ? playbackState.ballOwner : ballActor.ownerId;
    if (ownerId) {
      const owner = actors.find(
        (actor): actor is Exclude<Actor, { kind: 'ball' | 'area' }> =>
          actor.id === ownerId && actor.kind !== 'ball' && actor.kind !== 'area'
      );
      if (owner) {
        const ownerPos = getPosition(owner);
        return <circle cx={ownerPos.x} cy={ownerPos.y + 20 * markerScale} r={8 * markerScale} fill="#111827" stroke="#ffffff" strokeWidth={1} />;
      }
    }
    if (playbackState && playbackState.currentTime > 0) return null;
    if (typeof ballActor.x === 'number' && typeof ballActor.y === 'number') {
      const pos = getPosition(ballActor);
      return <circle cx={pos.x} cy={pos.y} r={8 * markerScale} fill="#111827" stroke="#ffffff" strokeWidth={1} />;
    }
    return null;
  };

  const renderArea = (actor: Extract<Actor, { kind: 'area' }>) => {
    if (!Array.isArray(actor.points) || actor.points.length < 3) return null;
    const points = actor.points.map((pt) => applyOrientation(pt, orientation));
    const d = points.map((pt, index) => `${index === 0 ? 'M' : 'L'} ${pt.x} ${pt.y}`).join(' ') + ' Z';
    return (
      <path
        key={actor.id}
        d={d}
        fill={actor.color ? `${actor.color}30` : 'rgba(34,197,94,0.18)'}
        stroke={actor.color || '#22c55e'}
        strokeWidth={2}
        opacity={0.6}
      />
    );
  };

  const renderActor = (actor: Exclude<Actor, { kind: 'ball' | 'area' }>) => {
    const position = getPosition(actor);

    // Timeline already interpolates motion. One instantaneous local frame keeps
    // each shape and label together under the Site's global transform transitions.
    if (actor.kind === 'player') {
      const fillColor = actor.team === 'A' ? '#2563eb' : actor.team === 'Neutral' ? '#ca8a04' : '#dc2626';
      return (
        <g key={actor.id} transform={`translate(${position.x} ${position.y}) scale(${markerScale})`} style={{ transition: 'none' }}>
          <g
            transform="translate(-18 -18) scale(1.5)"
            fill={fillColor}
            stroke="#ffffff"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M20.38 3.46 16 2a4 4 0 0 1-8 0L3.62 3.46a2 2 0 0 0-1.34 2.23l.58 3.47a1 1 0 0 0 .99.84H6v10c0 1.1.9 2 2 2h8a2 2 0 0 0 2-2V10h2.15a1 1 0 0 0 .99-.84l.58-3.47a2 2 0 0 0-1.34-2.23z" />
          </g>
          {showLabels && actor.number && (
            <text x={0} y={6} textAnchor="middle" fill="#fff" fontSize="12" fontWeight="bold">
              {actor.number}
            </text>
          )}
        </g>
      );
    }

    if (actor.kind === 'trainer') {
      return (
        <g key={actor.id} transform={`translate(${position.x} ${position.y}) scale(${markerScale})`} style={{ transition: 'none' }}>
          <g
            transform="translate(-18 -18) scale(1.5)"
            fill="#111827"
            stroke="#0f172a"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M20.38 3.46 16 2a4 4 0 0 1-8 0L3.62 3.46a2 2 0 0 0-1.34 2.23l.58 3.47a1 1 0 0 0 .99.84H6v10c0 1.1.9 2 2 2h8a2 2 0 0 0 2-2V10h2.15a1 1 0 0 0 .99-.84l.58-3.47a2 2 0 0 0-1.34-2.23z" />
          </g>
          {showLabels && (
            <text x={0} y={6} textAnchor="middle" fill="#fff" fontSize="12" fontWeight="bold">
              T
            </text>
          )}
        </g>
      );
    }

    if (actor.kind === 'keeper') {
      return (
        <g key={actor.id} transform={`translate(${position.x} ${position.y}) scale(${markerScale})`} style={{ transition: 'none' }}>
          <rect x={-18} y={-18} width={36} height={36} fill="#9333ea" stroke="#fff" strokeWidth={2} rx={4} />
          {showLabels && (
            <text x={0} y={6} textAnchor="middle" fill="#fff" fontSize="16" fontWeight="bold">
              K
            </text>
          )}
        </g>
      );
    }

    if (actor.kind === 'cone') {
      return <circle key={actor.id} cx={position.x} cy={position.y} r={8} fill="#e85a35" />;
    }

    if (actor.kind === 'pawn') {
      return (
        <polygon
          key={actor.id}
          points={`${position.x},${position.y - 12} ${position.x - 10},${position.y + 8} ${position.x + 10},${position.y + 8}`}
          fill="#eab308"
          stroke="#854d0e"
          strokeWidth={1.5}
        />
      );
    }

    if (actor.kind === 'ballpile') {
      return (
        <g key={actor.id}>
          <circle cx={position.x - 11} cy={position.y + 3} r={4.5} fill="#111827" stroke="#ffffff" strokeWidth={1} />
          <circle cx={position.x} cy={position.y + 3} r={4.5} fill="#111827" stroke="#ffffff" strokeWidth={1} />
          <circle cx={position.x + 11} cy={position.y + 3} r={4.5} fill="#111827" stroke="#ffffff" strokeWidth={1} />
          <circle cx={position.x - 5.5} cy={position.y - 5} r={4.5} fill="#111827" stroke="#ffffff" strokeWidth={1} />
          <circle cx={position.x + 5.5} cy={position.y - 5} r={4.5} fill="#111827" stroke="#ffffff" strokeWidth={1} />
        </g>
      );
    }

    if (actor.kind === 'miniGoal') {
      const angle = actor.angle ?? 0;
      const goalNumber = miniGoalNumberMap.get(actor.id);
      return (
        <g key={actor.id}>
          <g transform={`rotate(${angle} ${position.x} ${position.y})`} style={{ transition: 'none' }}>
            <line x1={position.x - 42} y1={position.y + 15} x2={position.x - 42} y2={position.y - 12} stroke="#334155" strokeWidth={3} />
            <line x1={position.x + 42} y1={position.y + 15} x2={position.x + 42} y2={position.y - 12} stroke="#334155" strokeWidth={3} />
            <line x1={position.x - 42} y1={position.y - 12} x2={position.x + 42} y2={position.y - 12} stroke="#334155" strokeWidth={3} />
            <line x1={position.x - 42} y1={position.y + 15} x2={position.x + 42} y2={position.y + 15} stroke="#94a3b8" strokeWidth={2} />
          </g>
          {showLabels && goalNumber && (
            <text x={position.x} y={position.y + 5} textAnchor="middle" fill="#0f172a" fontSize="14" fontWeight="700">
              {goalNumber}
            </text>
          )}
        </g>
      );
    }

    if (actor.kind === 'bar') {
      const angle = actor.angle ?? 0;
      return (
        <g key={actor.id} transform={`rotate(${angle} ${position.x} ${position.y})`} style={{ transition: 'none' }}>
          <rect x={position.x - 42} y={position.y - 6} width={84} height={12} rx={6} fill="#0f172a" />
        </g>
      );
    }

    return null;
  };

  return (
    <FieldView
      preset={preset}
      pitch={pitch}
      variant={variant}
      orientation={orientation}
      className={className}
      style={style}
    >
      <g>
        {actors
          .filter((actor): actor is Extract<Actor, { kind: 'area' }> => actor.kind === 'area')
          .map(renderArea)}
        {actors
          .filter((actor): actor is Exclude<Actor, { kind: 'area' | 'ball' }> => actor.kind !== 'area' && actor.kind !== 'ball')
          .map(renderActor)}
        {renderBall()}
      </g>
    </FieldView>
  );
}
